import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  OutlinedInput,
  MenuItem,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import AgentSelect from './AgentSelect';
import LeadSelect from './LeadSelect';
import { fetchLeadSelect, fetchAgentSelect } from '../../services/common';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';

import { useTask } from '../../contexts/TaskContext';
import { useAuth } from '../../contexts/AuthContext';
import DateTimeRangePicker from './DateTimeRangePicker';
import { getTaskPriorityColor, getTaskStatusColor } from '../../utils/helper';

dayjs.extend(utc);
dayjs.extend(timezone);

/* ── Theme tokens ── */
const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  ink2: '#253b2c',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  warnBg: '#fffbeb',
  warnBorder: '#fde68a',
  warnInk: '#92400e',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

/* ── Reusable sx objects ── */
/**
 * Section titles are outside the field; MUI still notches the outline when filled/focused.
 * Collapse the legend so the top border is one continuous line (overrides inline span width).
 */
const outlineLegendNoGap = {
  '& .MuiOutlinedInput-notchedOutline legend': {
    maxWidth: '0 !important',
    width: '0 !important',
    fontSize: 0,
    lineHeight: 0,
    padding: 0,
  },
  '& .MuiOutlinedInput-notchedOutline legend span': {
    padding: '0 !important',
    width: '0 !important',
    minWidth: '0 !important',
    display: 'block',
  },
};

const fieldSx = {
  ...outlineLegendNoGap,
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 2,
    bgcolor: T.white,
    '& fieldset': { borderColor: T.borderL },
    '&:hover fieldset': { borderColor: T.border },
    '&.Mui-focused fieldset': { borderColor: T.green },
    '&.Mui-error fieldset': { borderColor: T.red },
  },
  '& .MuiInputLabel-root': {
    fontFamily: T.font,
    fontSize: 13,
    color: T.subtle,
    overflow: 'visible',
    '&.Mui-focused': { color: T.green },
  },
  '& .MuiFormHelperText-root': { fontFamily: T.font, fontSize: 11.5, mt: 0.5 },
  '& .MuiOutlinedInput-input::placeholder': { fontFamily: T.font, opacity: 0.55 },
};

/**
 * MUI OutlinedInput defaults `notched` to true when the field is filled/focused so a floating
 * label can sit in the gap — even when there is no label. Section titles are outside the field,
 * so we always disable the notch.
 */
const outlinedInputNoNotch = { notched: false };

/** Select: MUI forces `notched: true` when `displayEmpty` — collapse legend (same as text fields) */
const selectSx = {
  ...outlineLegendNoGap,
  fontFamily: T.font,
  fontSize: 13,
  borderRadius: 2,
  bgcolor: T.white,
  '& fieldset': { borderColor: T.borderL },
  '&:hover fieldset': { borderColor: T.border },
  '&.Mui-focused fieldset': { borderColor: T.green },
  '& .MuiSelect-select': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    pr: '32px !important',
  },
};

const menuPaperSx = {
  sx: {
    fontFamily: T.font,
    borderRadius: 2,
    border: `1px solid ${T.borderL}`,
    boxShadow: '0 8px 24px rgba(15,26,19,0.08)',
    mt: 0.5,
    '& .MuiMenuItem-root': {
      fontFamily: T.font,
      fontSize: 13,
      py: 1,
      '&.Mui-selected': { bgcolor: T.greenLt, color: T.green, fontWeight: 600, '&:hover': { bgcolor: T.greenLt } },
      '&:hover': { bgcolor: T.bg },
    },
  },
};

const sectionLabelSx = {
  fontFamily: T.font,
  fontSize: 11,
  fontWeight: 700,
  color: T.muted,
  letterSpacing: '0.04em',
  mb: 1,
};

/** getTaskStatusColor omits some keys — fill gaps for pill fills */
const statusColorForPill = (key) => {
  const k = String(key || '').toLowerCase();
  const fromHelper = getTaskStatusColor(key);
  const fallback = {
    overdue: { border: '#EA580C', background: '#FFF7ED' },
    scheduled: { border: '#2563EB', background: '#EFF6FF' },
    update_pending: { border: '#7C3AED', background: '#F5F3FF' },
  };
  const fb = fallback[k];
  return {
    border: fromHelper?.border || fb?.border || '#64748B',
    background: fromHelper?.background || fb?.background || '#F8FAFC',
  };
};

const priorityColorForPill = (key) => {
  const fromHelper = getTaskPriorityColor(key);
  return {
    border: fromHelper?.border || '#6B7280',
    background: fromHelper?.background || '#F9FAFB',
  };
};

const statusPillSx = (active, key) => {
  const { border: activeColor } = statusColorForPill(key);
  return {
    fontFamily: T.font,
    textTransform: 'none',
    borderRadius: '999px',
    px: 1.25,
    py: '3px',
    fontSize: 11,
    fontWeight: active ? 600 : 500,
    minHeight: 24,
    lineHeight: 1.2,
    boxShadow: 'none',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    bgcolor: active ? activeColor : T.white,
    color: active ? '#fff' : T.ink2,
    border: `1px solid ${active ? activeColor : T.border}`,
    '&:hover': {
      boxShadow: 'none',
      bgcolor: active ? activeColor : T.bg,
      borderColor: active ? activeColor : T.subtle,
      color: active ? '#fff' : T.ink2,
    },
  };
};

const priorityPillSx = (active, key) => {
  const { border: activeColor } = priorityColorForPill(key);
  return {
    fontFamily: T.font,
    textTransform: 'none',
    borderRadius: '999px',
    px: 1.25,
    py: '3px',
    fontSize: 11,
    fontWeight: active ? 600 : 500,
    minHeight: 24,
    lineHeight: 1.2,
    boxShadow: 'none',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    bgcolor: active ? activeColor : T.white,
    color: active ? '#fff' : T.ink2,
    border: `1px solid ${active ? activeColor : T.border}`,
    '&:hover': {
      boxShadow: 'none',
      bgcolor: active ? activeColor : T.bg,
      borderColor: active ? activeColor : T.subtle,
      color: active ? '#fff' : T.ink2,
    },
  };
};

/** Default status order aligned with product UI (wrapping row flow) */
const DEFAULT_TASK_STATUSES = [
  { key: 'pending', label: 'Pending' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'update_pending', label: 'Update Pending' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'scheduled', label: 'Scheduled' },
];

/* ── SectionCard: bordered card with a labelled header strip ── */
const SectionCard = ({ icon, title, children }) => (
  <Box sx={{ border: `1px solid ${T.borderL}`, borderRadius: 2.5, overflow: 'hidden' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 2,
        py: 1,
        bgcolor: T.bg,
        borderBottom: `1px solid ${T.borderL}`,
      }}
    >
      {icon}
      <Typography sx={{ fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.subtle, letterSpacing: '0.06em' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 2, py: 1.75 }}>{children}</Box>
  </Box>
);

/* ── Component ── */
const CreateNewTask = React.memo(({ open, onClose, initialData = null, leadId = null, asset_id = null, lead = null, source = null, defaultDate = null, defaultItemType = null }) => {
  const isFormDisabled = initialData && ['completed', 'cancelled', 'overdue'].includes(initialData.status);

  const handleDateTimeChange = useCallback(({ startDateTime, endDateTime }) => {
    const startDate = dayjs(startDateTime);
    const now = dayjs();
    setFormData(prev => {
      const newData = { ...prev, start_time: startDateTime, end_time: endDateTime };
      if (startDate.isBefore(now, 'day') && prev.status === 'pending') {
        newData.status = 'completed';
      }
      return newData;
    });
  }, []);

  const { taskTypes, taskPriorities, taskStatuses, fetchTaskUtility, createTask, updateTask } = useTask();

  const [agents, setAgents] = useState([]);
  const { user } = useAuth();
  const [leadSelectOptions, setLeadSelectOptions] = useState([]);
  const [lead_id, setLeadId] = useState(leadId);

  const dataFetched = useRef({ agents: false, taskTypes: false, taskPriorities: false, leadSelectOptions: false });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!open) {
      dataFetched.current = { agents: false, taskTypes: false, taskPriorities: false, leadSelectOptions: false };
      return;
    }
    const fetchData = async () => {
      try {
        if ((!taskTypes?.length || !taskPriorities?.length) && !dataFetched.current.taskTypes) {
          await fetchTaskUtility();
          dataFetched.current.taskTypes = true;
          dataFetched.current.taskPriorities = true;
        }
        if (!leadId && !leadSelectOptions?.length && !dataFetched.current.leadSelectOptions) {
          const leadData = await fetchLeadSelect({ account_id: user?.account_id || user?.account?.id, user_id: user?.id });
          setLeadSelectOptions(leadData);
          dataFetched.current.leadSelectOptions = true;
        }
        if (!agents?.length && !dataFetched.current.agents) {
          const agentData = await fetchAgentSelect();
          setAgents(agentData);
          dataFetched.current.agents = true;
        }
      } catch (err) {
        console.error('Error fetching task data:', err);
      }
    };
    fetchData();
  }, [open, taskTypes?.length, taskPriorities?.length, agents?.length, leadSelectOptions?.length, leadId]);

  let leadIdValue = leadId;
  let assigned_to = initialData?.assigned_to?.id || initialData?.assigned_to?.agent_id || lead?.agent?.id || lead?.agent?.agent_id || null;
  if (!leadIdValue) {
    if (source === 'lead' || source === 'call') {
      leadIdValue = lead?.id;
    } else if (initialData?.lead) {
      leadIdValue = typeof initialData.lead === 'object' ? initialData.lead.id : initialData.lead;
    }
  }

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    item_type: '',
    priority: 'medium',
    start_time: dayjs(),
    end_time: dayjs().add(1, 'hour'),
    all_day: false,
    status: 'pending',
    assigned_to: assigned_to || null,
    lead: leadIdValue || null,
    asset_id: asset_id ? parseInt(asset_id) : null,
    project: null,
    location: '',
    is_virtual: false,
    meeting_link: '',
    visibility: 'private',
    color: '#4285F4',
    metadata: {},
  });

  const getDefaultTitle = (taskType, leadData = null) => {
    const baseTitles = {
      initial_contact: 'First Call with',
      lead_followup: 'Follow-up with',
      site_visit: 'Property Site Visit with',
      document_collection: 'Collect Documents from',
      general_task: 'General Task for',
      other: 'Other Task for',
    };
    const baseTitle = baseTitles[taskType] || 'Task for';
    if (leadData?.contact_info) {
      const contactName = leadData.contact_info.name || 'Lead';
      const contactInfo = leadData.contact_info.mobile || leadData.contact_info.email || '';
      return `${baseTitle} ${contactName}${contactInfo ? ` (${contactInfo})` : ''}`;
    }
    return baseTitle + ' Lead';
  };

  const getDefaultDescription = (taskType) => {
    const descs = {
      initial_contact: 'Initial contact call to understand requirements and introduce our services.',
      lead_followup: 'Follow-up call to discuss previous conversation and next steps.',
      site_visit: 'Property site visit to assess requirements and provide detailed consultation.',
      document_collection: 'Collect and verify required documents for the project.',
      general_task: 'General task to be completed.',
      other: 'Other task to be completed.',
    };
    return descs[taskType] || 'Task to be completed.';
  };

  useEffect(() => {
    if (taskTypes && taskTypes.length > 0 && !formData.item_type) {
      const generalTaskOption = taskTypes.find(o => o.key === 'general_task');
      const defaultOption = generalTaskOption || taskTypes[0];
      if (defaultOption) {
        setFormData(prev => {
          const newData = { ...prev, item_type: defaultOption.key };
          if (!prev.title) newData.title = getDefaultTitle(defaultOption.key, lead);
          if (!prev.description) newData.description = getDefaultDescription(defaultOption.key);
          return newData;
        });
      }
    }
  }, [taskTypes, formData.item_type]);

  useEffect(() => {
    if (!open) return;
    if (initialData && initialData.id) {
      setFormData({
        ...initialData,
        start_time: initialData.start_time ? dayjs(initialData.start_time) : dayjs(),
        end_time: initialData.end_time ? dayjs(initialData.end_time) : dayjs().add(1, 'hour'),
        color: initialData.metadata?.color || getTaskPriorityColor(initialData.priority).border || '#4285F4',
        description: initialData.description || '',
        status: initialData.status || 'pending',
        lead: initialData.lead !== undefined ? initialData.lead : null,
      });
    } else {
      const defaultStartTime = defaultDate ? dayjs(defaultDate) : dayjs();
      const defaultEndTime = defaultDate ? dayjs(defaultDate).add(1, 'hour') : dayjs().add(1, 'hour');
      const preset = defaultItemType && String(defaultItemType).trim();
      setFormData({
        title: preset ? getDefaultTitle(preset, lead) : '',
        description: preset ? getDefaultDescription(preset) : '',
        item_type: preset || '',
        priority: 'medium',
        start_time: defaultStartTime,
        end_time: defaultEndTime,
        all_day: false,
        status: 'pending',
        assigned_to: assigned_to,
        participants: [],
        lead: leadIdValue,
        asset_id: asset_id ? parseInt(asset_id) : null,
        project: null,
        color: '#4285F4',
        metadata: {},
      });
    }
  }, [open, initialData?.id, lead_id, asset_id, lead?.id, source, defaultDate, defaultItemType]);

  const shouldShowLeadField = () => source !== 'lead' && source !== 'call';

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value,
        ...(name === 'priority' ? { color: getTaskPriorityColor(value).border || prev.color } : {}),
      };
      if (name === 'item_type' && value) {
        newData.title = getDefaultTitle(value, lead);
        newData.description = getDefaultDescription(value);
      }
      return newData;
    });
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.title?.trim()) errors.title = 'Title is required';
    if (!formData.description?.trim()) errors.description = 'Description is required';
    if (formData.item_type === 'meeting') {
      if (!formData.participants || formData.participants.length === 0) errors.participants = 'Please select at least one participant';
    } else {
      if (!formData.assigned_to) errors.assigned_to = 'Please assign this task to someone';
    }
    if (formData.start_time && formData.end_time && dayjs(formData.start_time).isAfter(dayjs(formData.end_time))) {
      errors.end_time = 'End time must be after start time';
    }
    if (['followup', 'contact_call', 'sitevisit'].includes(formData.item_type) && !formData.lead) {
      errors.lead = 'Lead is required for this task type';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    setLoading(true);
    setError(null);
    try {
      const formattedData = {
        ...formData,
        start_time: formData.start_time,
        end_time: formData.end_time,
        metadata: { ...formData.metadata, color: formData.color },
      };
      if (formattedData.assigned_to) {
        formattedData.assigned_to = typeof formattedData.assigned_to === 'object'
          ? (formattedData.assigned_to.id || formattedData.assigned_to.agent_id)
          : formattedData.assigned_to;
      }
      if (formattedData.lead !== null && formattedData.lead !== undefined) {
        formattedData.lead = typeof formattedData.lead === 'object' ? formattedData.lead.id : formattedData.lead;
      } else {
        formattedData.lead = leadIdValue || null;
      }
      if (formattedData.asset_id) formattedData.asset_id = parseInt(formattedData.asset_id) || null;
      delete formattedData.color;
      if (formattedData.lead === undefined) formattedData.lead = null;
      if (initialData?.id && initialData?.lead && formattedData.lead === null) {
        formattedData.lead = typeof initialData.lead === 'object' ? initialData.lead.id : initialData.lead;
      }
      if (initialData?.id) {
        await updateTask(initialData.id, formattedData);
      } else {
        await createTask(lead_id, formattedData, asset_id);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.non_field_errors || 'Failed to save task');
      if (err.response?.data?.errors) setFormErrors(err.response.data.errors);
    } finally {
      setLoading(false);
    }
  }, [formData, initialData, lead_id, asset_id, updateTask, createTask, onClose, validateForm]);

  useEffect(() => {
    if (initialData) {
      if (initialData.item_type === 'meeting') {
        setFormData(prev => ({ ...prev, participants: Array.isArray(initialData.participants) ? initialData.participants : [] }));
      }
    } else {
      setFormData(prev => ({ ...prev, participants: [] }));
    }
  }, [initialData]);

  useEffect(() => {
    if (formData.item_type === 'meeting' && !Array.isArray(formData.participants)) {
      setFormData(prev => ({ ...prev, participants: [] }));
    }
  }, [formData.item_type]);

  const secIconSx = { fontSize: 15, color: T.muted };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          maxWidth: 580,
          width: '100%',
          borderRadius: 3,
          fontFamily: T.font,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(15,26,19,0.14), 0 2px 8px rgba(15,26,19,0.06)',
          m: 2,
        },
      }}
      onContextMenu={(e) => { if (isFormDisabled) e.preventDefault(); }}
    >
      {/* ── Header ── */}
      <Box sx={{ px: 3, py: 2.25, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '12px',
              bgcolor: T.greenLt,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AssignmentOutlinedIcon sx={{ fontSize: 22, color: T.green }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 17, fontWeight: 700, color: T.ink, lineHeight: 1.3, letterSpacing: '-0.01em' }}>
              {initialData ? 'Edit task' : 'New task'}
            </Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: 12, color: T.muted, mt: 0.15, lineHeight: 1.5 }}>
              {initialData ? 'Update details, schedule, and assignment' : 'Define what needs to happen, when, and who owns it'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          disabled={loading}
          sx={{ color: T.muted, mt: 0.25, '&:hover': { color: T.ink, bgcolor: T.greenSf }, flexShrink: 0, borderRadius: 2 }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {isFormDisabled && (
        <Box sx={{ mx: 3, mb: 0.5, p: 1.25, borderRadius: 2, border: `1px solid ${T.warnBorder}`, bgcolor: T.warnBg, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: 12, color: T.warnInk, fontWeight: 600 }}>
            Read-only
          </Typography>
          <Typography sx={{ fontFamily: T.font, fontSize: 12, color: T.warnInk }}>
            — This task is <strong>{initialData?.status}</strong> and can no longer be modified.
          </Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: T.borderL }} />

      {/* ── Content ── */}
      <DialogContent sx={{ px: 3, pt: 2.5, pb: 2 }}>
        {error && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: `1px solid ${T.redBorder}`, bgcolor: T.redLt }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red, fontWeight: 500 }}>{error}</Typography>
          </Box>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* ── Task Details ── */}
            <SectionCard icon={<AssignmentOutlinedIcon sx={secIconSx} />} title="Task Details">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography sx={sectionLabelSx}>Event Type</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      name="item_type"
                      value={formData.item_type}
                      onChange={handleChange}
                      disabled={isFormDisabled}
                      displayEmpty
                      input={<OutlinedInput notched={false} />}
                      sx={selectSx}
                      MenuProps={{ PaperProps: menuPaperSx }}
                    >
                      <MenuItem value="" disabled sx={{ fontFamily: T.font, fontSize: 13, color: T.muted }}>
                        Choose a task type…
                      </MenuItem>
                      {taskTypes && taskTypes.length > 0 ? (
                        taskTypes.map(option => (
                          <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                        ))
                      ) : (
                        [
                          <MenuItem key="initial_contact" value="initial_contact">Initial Contact</MenuItem>,
                          <MenuItem key="lead_followup" value="lead_followup">Lead Follow-up</MenuItem>,
                          <MenuItem key="site_visit" value="site_visit">Site Visit</MenuItem>,
                          <MenuItem key="document_collection" value="document_collection">Document Collection</MenuItem>,
                          <MenuItem key="general_task" value="general_task">General Task</MenuItem>,
                          <MenuItem key="other" value="other">Other</MenuItem>,
                        ]
                      )}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography sx={sectionLabelSx}>Title</Typography>
                  <TextField
                    name="title"
                    placeholder="e.g. Follow-up call with lead"
                    value={formData.title}
                    onChange={handleChange}
                    disabled={isFormDisabled}
                    fullWidth
                    variant="outlined"
                    size="small"
                    error={!!formErrors.title}
                    helperText={formErrors.title}
                    sx={fieldSx}
                    slotProps={{ input: outlinedInputNoNotch }}
                    InputProps={outlinedInputNoNotch}
                  />
                </Box>
              </Box>
            </SectionCard>

            {/* ── Assignment ── */}
            <SectionCard icon={<PersonOutlineIcon sx={secIconSx} />} title="Assignment">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography sx={sectionLabelSx}>Assigned agent</Typography>
                  <AgentSelect
                    agents={agents}
                    value={formData.assigned_to?.id || formData.assigned_to || null}
                    onChange={(agentId, selectedAgent) => {
                      setFormData(prev => ({ ...prev, assigned_to: selectedAgent }));
                      if (formErrors.assigned_to) setFormErrors(prev => ({ ...prev, assigned_to: null }));
                    }}
                    variant="assign"
                    multiple={false}
                    hideInputLabel
                    sx={outlineLegendNoGap}
                    error={formErrors.assigned_to}
                    helperText={formErrors.assigned_to || 'Choose who will own this task'}
                    required
                    disabled={isFormDisabled}
                  />
                </Box>

                {shouldShowLeadField() && (
                  <Box>
                    <Typography sx={sectionLabelSx}>Linked lead</Typography>
                    <LeadSelect
                      label=""
                      placeholder="Search or select a lead…"
                      sx={outlineLegendNoGap}
                      value={formData.lead}
                      onChange={(leadSelectId, selectedLead) => {
                        if (selectedLead !== undefined && selectedLead !== null) {
                          setFormData(prev => ({ ...prev, lead: selectedLead }));
                          if (formErrors.lead) setFormErrors(prev => ({ ...prev, lead: null }));
                        } else {
                          setFormData(prev => ({ ...prev, lead: null }));
                          if (formErrors.lead) setFormErrors(prev => ({ ...prev, lead: null }));
                        }
                      }}
                      error={formErrors.lead}
                      required={['followup', 'contact_call', 'sitevisit'].includes(formData.item_type)}
                      options={leadSelectOptions || []}
                      disabled={loading || isFormDisabled}
                    />
                  </Box>
                )}
              </Box>
            </SectionCard>

            {/* ── Schedule ── */}
            <SectionCard icon={<EventNoteOutlinedIcon sx={secIconSx} />} title="Schedule">
              <DateTimeRangePicker
                initialStartDateTime={formData.start_time}
                initialEndDateTime={formData.end_time}
                onChange={handleDateTimeChange}
                disabled={isFormDisabled}
              />
            </SectionCard>

            {/* ── Status & Priority: row 1 = status chips, row 2 = priority chips (both wrap) ── */}
            <SectionCard icon={<FlagOutlinedIcon sx={secIconSx} />} title="Status & Priority">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                <Box>
                  <Typography sx={sectionLabelSx}>Status</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      alignContent: 'flex-start',
                    }}
                  >
                    {(taskStatuses && taskStatuses.length > 0 ? taskStatuses : DEFAULT_TASK_STATUSES).map((status) => (
                      <Button
                        key={status.key}
                        variant="outlined"
                        size="small"
                        onClick={() => handleChange({ target: { name: 'status', value: status.key } })}
                        disabled={isFormDisabled}
                        sx={statusPillSx(formData.status === status.key, status.key)}
                      >
                        {status.label}
                      </Button>
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography sx={sectionLabelSx}>Priority</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      alignContent: 'flex-start',
                    }}
                  >
                    {(taskPriorities || [
                      { key: 'low', label: 'Low' },
                      { key: 'medium', label: 'Medium' },
                      { key: 'high', label: 'High' },
                      { key: 'urgent', label: 'Urgent' },
                    ]).map((priority) => (
                      <Button
                        key={priority.key}
                        variant="outlined"
                        size="small"
                        onClick={() => handleChange({ target: { name: 'priority', value: priority.key } })}
                        disabled={isFormDisabled}
                        sx={priorityPillSx(formData.priority === priority.key, priority.key)}
                      >
                        {priority.label}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Box>
            </SectionCard>

            {/* ── Notes / Description ── */}
            <SectionCard icon={<NotesOutlinedIcon sx={secIconSx} />} title="Notes">
              <Box>
                <TextField
                  name="description"
                  placeholder="What needs to happen, any context, and next steps…"
                  value={formData.description || ''}
                  onChange={handleChange}
                  disabled={isFormDisabled}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!formErrors.description}
                  helperText={formErrors.description}
                  sx={fieldSx}
                  slotProps={{ input: outlinedInputNoNotch }}
                  InputProps={outlinedInputNoNotch}
                />
              </Box>
            </SectionCard>

          </Box>
        </LocalizationProvider>
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
          borderTop: `1px solid ${T.borderL}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 500,
            color: T.subtle,
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            '&:hover': { bgcolor: T.greenSf, color: T.ink },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || isFormDisabled}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            minWidth: 130,
            height: 38,
            bgcolor: T.green,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          {loading ? (
            <CircularProgress size={20} thickness={4} sx={{ color: '#fff' }} />
          ) : initialData ? (
            'Save changes'
          ) : (
            'Create task'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.open === nextProps.open &&
    prevProps.leadId === nextProps.leadId &&
    prevProps.asset_id === nextProps.asset_id &&
    prevProps.source === nextProps.source &&
    prevProps.defaultDate === nextProps.defaultDate &&
    JSON.stringify(prevProps.initialData) === JSON.stringify(nextProps.initialData) &&
    JSON.stringify(prevProps.lead) === JSON.stringify(nextProps.lead) &&
    prevProps.defaultItemType === nextProps.defaultItemType
  );
});

export default CreateNewTask;
